import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterModule,
  Routes,
} from "@angular/router";
import { AppService } from "../apps/app.service";
import { LoggedInGuard } from "../auth/auth.guards";
import { SessionService } from "../session.service";
import { ChartsService } from "../shared/charts.service";
import { ExecutiveSummaryComponent } from "./executive-summary/executive-summary.component";
import { HeatMapComponent } from "./heat-map/heat-map.component";
import { RedemptionsComponent } from "./redemptions/redemptions.component";
import { RegionsComponent } from "./regions/regions.component";
import { GeofenceService } from "../geofences/geofence.service";
import { take } from "rxjs/operators";
import { BroadcastService } from "../broadcasts/broadcast.service";
import { FlowsService } from "../flows/flows.service";
import { DeviceInfoComponent } from "./device-info/device-info.component";
import { InsightsComponent } from "./insights/insights.component";
import { SignalsService } from "../signals/signals.service";

@Injectable({ providedIn: "root" })
export class StartDateResolve implements Resolve<any> {
  constructor() {}

  resolve() {
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    start.setHours(0, 0, 0, 0);
    return start;
  }
}

@Injectable({ providedIn: "root" })
export class EndDateResolve implements Resolve<any> {
  constructor() {}

  resolve() {
    const end = new Date();
    end.setHours(23, 59, 59);
    return end;
  }
}

@Injectable({ providedIn: "root" })
export class ExecutiveSummaryResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const facilityIDs = this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return facility.id;
        })
      : [];

    const start = route.parent!.data.start;
    const end = route.parent!.data.end;
    return this.AppService.getExecutiveSummary(
      this.SessionService.currentAccountValue!.accountID,
      facilityIDs,
      start,
      end,
    );
  }
}

@Injectable({ providedIn: "root" })
export class HeatMapResolve implements Resolve<any> {
  constructor(
    private ChartsService: ChartsService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const facilityIDs = this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return facility.id;
        })
      : [];

    const start = route.parent!.data.start;
    const end = route.parent!.data.end;
    return this.ChartsService.getHeatMap(
      this.SessionService.currentAccountValue!.accountID,
      {
        startdate: start,
        enddate: end,
        facility_id: facilityIDs,
        type: "heat-map-2",
      },
    );
  }
}

@Injectable({ providedIn: "root" })
export class GeofencesResolve implements Resolve<any> {
  constructor(
    private GeofencesService: GeofenceService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    const facilityIDs = this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return facility.id;
        })
      : [];

    return this.GeofencesService.getGeofences(
      this.SessionService.currentAccountValue!.accountID,
      {
        facility_id: facilityIDs,
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class RegionsResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const start = route.parent!.data.start;
    const end = route.parent!.data.end;

    return this.AppService.getRegions(
      this.SessionService.currentAccountValue!.accountID,
      start,
      end,
      false
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class CompareRegionGroupsResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.AppService.getRegionCompareGroups(
      this.SessionService.currentUserValue!.id,
      this.SessionService.currentAppValue!.id,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class RedemptionsResolve implements Resolve<any> {
  constructor(
    private SignalsService: SignalsService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const start: Date = route.parent!.data.start;
    const end: Date = route.parent!.data.end;

    return this.SignalsService.getRedemptionsReport(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        start: start.toISOString(),
        end: end.toISOString(),
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class BroadcastRedemptionsResolve implements Resolve<any> {
  constructor(
    private BroadcastService: BroadcastService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const start: Date = route.parent!.data.start;
    const end: Date = route.parent!.data.end;

    return this.BroadcastService.getRedemptions(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        start: start.toISOString(),
        end: end.toISOString(),
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class FlowRedemptionsResolve implements Resolve<any> {
  constructor(
    private FlowsService: FlowsService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const start: Date = route.parent!.data.start;
    const end: Date = route.parent!.data.end;

    return this.FlowsService.getRedemptions(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        start: start.toISOString(),
        end: end.toISOString(),
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AndroidDeviceInfoResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const start = route.parent!.data.start;
    const end = route.parent!.data.end;

    return this.AppService.getDeviceInfo(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      "android",
      start,
      end,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class IOSDeviceInfoResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const start = route.parent!.data.start;
    const end = route.parent!.data.end;

    return this.AppService.getDeviceInfo(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      "ios",
      start,
      end,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class InsightsResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const start = route.parent!.data.start;
    const end = route.parent!.data.end;

    return this.AppService.getInsights(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      start,
      end,
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "intelligence" },
    children: [
      {
        path: "executive-summary",
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: "",
            data: { title: "Executive Summary" },
            component: ExecutiveSummaryComponent,
            resolve: {
              executiveSummary: ExecutiveSummaryResolve,
            },
          },
        ],
      },
      {
        path: "redemptions",
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: "",
            data: { title: "Redemptions" },
            component: RedemptionsComponent,
            resolve: {
              redemptions: RedemptionsResolve,
            },
          },
        ],
      },
      {
        path: "heatMap",
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: "",
            data: { title: "Heat Map" },
            component: HeatMapComponent,
            resolve: {
              heatMap: HeatMapResolve,
            },
          },
        ],
      },
      {
        path: "regions",
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: "",
            data: { title: "Regions" },
            component: RegionsComponent,
            resolve: {
              regions: RegionsResolve,
              compareRegionGroups: CompareRegionGroupsResolve,
            },
          },
        ],
      },
      {
        path: "deviceInfo",
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: "",
            data: { title: "Device Info" },
            component: DeviceInfoComponent,
            resolve: {
              androidDeviceInfo: AndroidDeviceInfoResolve,
              iosDeviceInfo: IOSDeviceInfoResolve,
            },
          },
        ],
      },
      {
        path: "insights",
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: "",
            data: { title: "Insights" },
            component: InsightsComponent,
            resolve: {
              insights: InsightsResolve,
            },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class IntelligenceRoutingModule {}

const intelligenceModuleLazyLoadRoutes: Routes = [
  {
    path: "intelligence",
    loadChildren: () =>
      import("./intelligence.module").then((m) => m.IntelligenceModule),
  },
];

export { intelligenceModuleLazyLoadRoutes };
